import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FloorPlanRoomType, SitePlanBuilding } from "../../utils/floorPlanHelpers";
import styles from "./SideNavigation.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

export interface SideNavigationProps {
  roomTypes: FloorPlanRoomType[];
  sitePlan: SitePlanBuilding[];
  hasPropertyLayout?: boolean;
  activeRoomTypeId: string | null;
  isSitePlanActive: boolean;
  onRoomTypeSelect: (id: string) => void;
  onSitePlanSelect: () => void;
}

const SideNavigation = ({
  roomTypes,
  sitePlan,
  hasPropertyLayout = false,
  activeRoomTypeId,
  isSitePlanActive,
  onRoomTypeSelect,
  onSitePlanSelect,
}: SideNavigationProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [showFade, setShowFade] = useState(false);

  const hasSitePlan = sitePlan.length > 0 || hasPropertyLayout;

  // Build a flat list of navigable items for arrow key support
  const navItems = useMemo(() => {
    const items: { type: "site_plan" | "room_type"; id: string }[] = [];
    if (hasSitePlan) {
      items.push({ type: "site_plan", id: "site_plan" });
    }
    roomTypes.forEach((rt) => {
      items.push({ type: "room_type", id: rt.id });
    });
    return items;
  }, [hasSitePlan, roomTypes]);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowFade(hasOverflow && !isAtBottom);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", checkOverflow);
    window.addEventListener("resize", checkOverflow);
    return () => {
      el.removeEventListener("scroll", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow, roomTypes]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      let targetIndex: number | null = null;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        targetIndex = index < navItems.length - 1 ? index + 1 : 0;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        targetIndex = index > 0 ? index - 1 : navItems.length - 1;
      } else if (e.key === "Home") {
        e.preventDefault();
        targetIndex = 0;
      } else if (e.key === "End") {
        e.preventDefault();
        targetIndex = navItems.length - 1;
      }

      if (targetIndex !== null) {
        buttonRefs.current[targetIndex]?.focus();
        const item = navItems[targetIndex];
        if (item.type === "site_plan") {
          onSitePlanSelect();
        } else {
          onRoomTypeSelect(item.id);
        }
      }
    },
    [navItems, onRoomTypeSelect, onSitePlanSelect],
  );

  // Determine which item index is currently active
  const activeIndex = useMemo(() => {
    if (isSitePlanActive && hasSitePlan) return 0;
    return navItems.findIndex((item) => item.type === "room_type" && item.id === activeRoomTypeId);
  }, [isSitePlanActive, hasSitePlan, navItems, activeRoomTypeId]);
  const { t } = useTranslationHook();

  return (
    <nav
      className={styles.container}
      aria-label="Floor plan navigation"
      data-testid="floor-plans-side-navigation"
    >
      <div ref={scrollRef} className={styles.scrollArea}>
        {hasSitePlan && (
          <>
            <div className={styles.separator}>
              {t("propertypage.floorplanssection.sidenavigation.unitlayout")}
            </div>
            <button
              type="button"
              ref={(el) => {
                buttonRefs.current[0] = el;
              }}
              className={`${styles.sitePlanButton} ${isSitePlanActive ? styles.active : ""}`.trim()}
              onClick={onSitePlanSelect}
              onKeyDown={(e) => handleKeyDown(e, 0)}
              aria-label="Select Site Plan"
              aria-current={isSitePlanActive ? "true" : undefined}
              tabIndex={activeIndex === 0 ? 0 : -1}
              data-testid="floor-plans-site-plan-button"
            >
              {t("propertypage.floorplanssection.sidenavigation.siteplan")}
            </button>
          </>
        )}
        {hasSitePlan && roomTypes.length > 0 && (
          <div className={styles.separator}>
            {t("propertypage.floorplanssection.sidenavigation.roomlayout")}
          </div>
        )}
        {roomTypes.map((roomType) => {
          const idx = navItems.findIndex((item) => item.id === roomType.id);
          const isActive = activeRoomTypeId === roomType.id && !isSitePlanActive;
          return (
            <button
              type="button"
              key={roomType.id}
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              className={`${styles.roomTypeButton} ${isActive ? styles.active : ""}`.trim()}
              onClick={() => onRoomTypeSelect(roomType.id)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              aria-label={`Select ${roomType.name} floor plan`}
              aria-current={isActive ? "true" : undefined}
              tabIndex={activeIndex === idx ? 0 : -1}
              data-testid={`floor-plans-room-type-${roomType.id}`}
            >
              {roomType.name}
            </button>
          );
        })}
      </div>
      {showFade && <div className={styles.fadeOverlay} aria-hidden="true" />}
    </nav>
  );
};

export default wrapperHOC(SideNavigation, {
  componentName: "SideNavigation-FloorPlansSection-PropertyPage",
  showForChina: true,
});
